type AnyRecord = Record<string, any>

function safeJsonParse(value: unknown): unknown {
    if (typeof value !== "string") return undefined
    try {
        return JSON.parse(value)
    } catch {
        return undefined
    }
}

/** Reads only the `message` field from a typical API envelope, e.g. `{ statusCode, status, message, data }`. */
function readApiBodyMessage(record: unknown): string | undefined {
    if (!record || typeof record !== "object") return undefined
    const msg = (record as Record<string, unknown>).message
    if (typeof msg === "string" && msg.trim() !== "") return msg.trim()
    if (typeof msg === "number" && !Number.isNaN(msg)) return String(msg)
    return undefined
}

export type ApiErrorInfo = {
    message: string
    httpStatus?: number
    raw?: unknown
}

/**
 * Normalizes errors coming from our axios interceptor (`api-clients.ts`) which wraps
 * errors as `new Error(JSON.stringify({...response.data, httpError: error}))`.
 */
export function getApiErrorInfo(error: unknown, fallbackMessage = "Something went wrong"): ApiErrorInfo {
    const err = error as AnyRecord | undefined

    const parsedFromMessage = safeJsonParse(err?.message)
    const parsed =
        (parsedFromMessage && typeof parsedFromMessage === "object" ? (parsedFromMessage as AnyRecord) : undefined) ??
        err

    const httpStatus: number | undefined =
        err?.response?.status ??
        err?.httpError?.response?.status ??
        parsed?.httpError?.response?.status ??
        parsed?.httpError?.status ??
        parsed?.httpStatus

    const fromResponseData = readApiBodyMessage(err?.response?.data)
    const fromInterceptorEnvelope =
        parsedFromMessage && typeof parsedFromMessage === "object" ? readApiBodyMessage(parsedFromMessage) : undefined
    const rawErrMsg = typeof err?.message === "string" ? err.message : undefined
    const plainErrMsg = rawErrMsg && !rawErrMsg.trim().startsWith("{") ? rawErrMsg : undefined

    const message = (fromResponseData ?? fromInterceptorEnvelope ?? plainErrMsg ?? fallbackMessage) as string

    return { message, httpStatus, raw: parsedFromMessage ?? error }
}

/**
 * Recursively extracts a human-readable error message from an unknown value.
 * Handles strings, arrays, and objects with common error-message keys.
 */
export function extractErrorMessage(value: unknown): string {
    if (typeof value === "string") return value
    if (Array.isArray(value)) {
        for (const item of value) {
            const msg = extractErrorMessage(item)
            if (msg) return msg
        }
        return ""
    }
    if (value && typeof value === "object") {
        const obj = value as Record<string, unknown>
        for (const key of ["message", "errorMessage", "error", "detail", "statusMessage"]) {
            if (typeof obj[key] === "string") return obj[key] as string
        }
        return JSON.stringify(value)
    }
    return String(value ?? "")
}

/**
 * Checks a 200-OK API response body for embedded errors.
 * Looks for `errors`, `error`, `errorMessage` at the top level and inside `data`.
 * Returns the extracted message string, or `null` if no error was found.
 */
export function getResponseError(result: unknown): string | null {
    if (!result || typeof result !== "object") return null
    const res = result as Record<string, unknown>
    const nested = typeof res.data === "object" && res.data !== null ? (res.data as Record<string, unknown>) : null

    for (const key of ["errors", "error", "errorMessage"]) {
        const val = res[key] ?? nested?.[key]
        if (val != null && val !== "" && !(Array.isArray(val) && val.length === 0)) {
            return extractErrorMessage(val) || null
        }
    }
    return null
}
